import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { HTTPResponse } from '@/class/HTTPResponse';
import { useAuthContext } from '../../context/AuthContext';
import { fetchData } from '../../lib/api';
import { adminCardAccent, adminMutedText, adminSectionTitle } from '../layout/adminStyles';

interface CompletionChartItem {
  date: string;
  count: number;
}

interface DailyCompletion {
  completion_date: string;
  completed_count: number;
}

const formatChartDate = (completionDate: string): string => {
  const date = new Date(`${completionDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return completionDate;
  }

  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'Asia/Tokyo',
  });
};

const CompletionChart: React.FC = () => {
  const { store } = useAuthContext();
  const [chartData, setChartData] = useState<CompletionChartItem[]>([]);
  const [completedDays, setCompletedDays] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMonthlyCompletions = useCallback(async () => {
    if (!store?.id) {
      setChartData([]);
      setCompletedDays(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/daily-completion/monthly`);
      const completions = Array.isArray(res.data?.completions)
        ? (res.data.completions as DailyCompletion[])
        : [];

      setChartData(
        completions.map((item) => ({
          date: formatChartDate(item.completion_date),
          count: item.completed_count,
        }))
      );
      setCompletedDays(
        completions.reduce((total, item) => total + item.completed_count, 0)
      );
    } catch (err: unknown) {
      console.error(err);
      setChartData([]);
      setCompletedDays(0);
      setErrorMessage('更新完了日数の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadMonthlyCompletions();
  }, [loadMonthlyCompletions]);

  return (
    <div className={`${adminCardAccent} border-l-violet-400 bg-gradient-to-br from-white to-violet-50/20`}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <h4 className={`${adminSectionTitle} mb-0 border-l-violet-400 pb-0`}>
          更新完了日数の推移（過去31日）
        </h4>
        {!isLoading && !errorMessage && (
          <p className="text-sm text-violet-700">
            完了日数: <span className="text-lg font-semibold text-stone-800">{completedDays}</span> 日
          </p>
        )}
      </div>

      {isLoading ? (
        <p className={`${adminMutedText} text-sm`}>読み込み中...</p>
      ) : errorMessage ? (
        <p className={`${adminMutedText} text-sm`}>{errorMessage}</p>
      ) : chartData.length === 0 ? (
        <p className={`${adminMutedText} text-sm`}>表示できる更新完了データがありません。</p>
      ) : (
        <div className="h-[220px] sm:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="date" tick={{ fill: '#78716c', fontSize: 12 }} />
              <YAxis
                allowDecimals={false}
                domain={[0, 1]}
                ticks={[0, 1]}
                tick={{ fill: '#78716c', fontSize: 12 }}
                label={{ value: '完了', angle: -90, position: 'insideLeft', fill: '#57534e', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid #e7e5e4',
                  backgroundColor: '#fff',
                }}
              />
              <Line
                type="stepAfter"
                dataKey="count"
                name="更新完了"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ fill: '#a78bfa', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#7c3aed' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CompletionChart;
